import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, todayISO } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const inputCls = "h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-slate-900 transition-colors";

const LeaveForm = ({ workers, onSaved }) => {
  const [f, setF] = useState({ worker_id: "", start: todayISO(), end: todayISO(), note: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!f.worker_id) return toast.error("Select a worker");
    setBusy(true);
    try { await api.post("/leaves", f); toast.success("Sick leave added"); setF({ ...f, note: "" }); onSaved(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-xl p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
      <label className="space-y-1 lg:col-span-1"><span className="block text-xs font-medium text-slate-600">Worker</span>
        <Select value={f.worker_id} onValueChange={(v) => setF({ ...f, worker_id: v })}>
          <SelectTrigger className="h-10 bg-white" data-testid="leave-worker-select"><SelectValue placeholder="Select worker" /></SelectTrigger>
          <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
        </Select>
      </label>
      <label className="space-y-1"><span className="block text-xs font-medium text-slate-600">From</span>
        <input type="date" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="leave-start-input" className={`${inputCls} w-full`} /></label>
      <label className="space-y-1"><span className="block text-xs font-medium text-slate-600">To</span>
        <input type="date" required min={f.start} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="leave-end-input" className={`${inputCls} w-full`} /></label>
      <label className="space-y-1"><span className="block text-xs font-medium text-slate-600">Note</span>
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Medical certificate" data-testid="leave-note-input" className={`${inputCls} w-full`} /></label>
      <button disabled={busy} data-testid="leave-add-btn" className="h-10 px-5 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
        <Plus size={16} /> Add sick leave
      </button>
    </form>
  );
};

export default function SickLeave() {
  const [year, setYear] = useState(Number(todayISO().slice(0, 4)));
  const [workers, setWorkers] = useState([]);
  const [list, setList] = useState([]);

  const load = useCallback(() => api.get("/leaves", { params: { year } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [year]);
  useEffect(() => { api.get("/workers").then((r) => setWorkers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Remove sick leave for ${l.worker_name} (${l.start} → ${l.end})?`)) return;
    try { await api.delete(`/leaves/${l.id}`); toast.success("Sick leave removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const total = list.reduce((s, l) => s + l.days, 0);
  const today = todayISO();
  return (
    <>
      <PageHeader overline={`${list.length} leaves · ${total} days in ${year}`} title="Sick Leave">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="leave-year-select" className={inputCls}>
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </PageHeader>
      <LeaveForm workers={workers} onSaved={load} />
      <p className="text-sm text-slate-500 -mt-4">Days on sick leave are not counted as absent. Total sick days per worker appear in the Monthly Summary.</p>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaves-table">
          <thead className="text-left text-xs uppercase text-slate-500 font-mono bg-slate-50">
            <tr><th className="px-5 py-3">Worker</th><th className="px-5 py-3">From</th><th className="px-5 py-3">To</th><th className="px-5 py-3 text-right">Days</th><th className="px-5 py-3">Note</th><th className="px-5 py-3" /></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">No sick leave recorded for {year}.</td></tr>}
            {list.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`leave-row-${l.id}`}>
                <td className="px-5 py-3"><p className="font-medium">{l.worker_name}</p><p className="font-mono text-xs text-slate-400">{l.worker_code}</p></td>
                <td className="px-5 py-3 font-mono">{l.start}</td>
                <td className="px-5 py-3 font-mono">{l.end}{l.start <= today && today <= l.end && <span className="ml-2 px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold">ONGOING</span>}</td>
                <td className="px-5 py-3 text-right font-mono font-bold text-fuchsia-600">{l.days}</td>
                <td className="px-5 py-3 text-slate-600">{l.note || "—"}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => remove(l)} data-testid={`leave-delete-btn-${l.id}`} className="p-2 rounded-md hover:bg-rose-100 text-rose-600 transition-colors"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
